import React, { useState, useRef, useEffect } from 'react';
import './web-form.sass';
import socketIOClient from 'socket.io-client';
import Loader from 'react-spinners/BounceLoader';
import SurveySubmitted from './SurveySubmitted';
import { Navigate } from 'react-router-dom';
import Recaptcha from 'react-recaptcha';
import { getNestedObjectSafely } from '../utils/functions';
import { getQuestionData, fetchBusinessData } from './web-form.service';
import NpsQuestion from './npsQuestion';
import CESQuestion from './cesQuestion';
import RangeQuestion from './RangeQuestion';
import OpenEnded from './openEnded';
import OpenEndedDate from './openEndedDate';
import YearQuestion from './yearQuestion';
import SingleSelect from './singleSelect';
import MultipleSelect from './multipleSelect';
import FallBack from './fallback';
import CSAT from './CSAT';
import ResolutionScore from './ResolutionScore';
import IntegerChoice from './IntegerChoice';
import logo from './assets/ajua_logo.png';
import axios from 'axios';

const INITIAL_ERROR_QUESTION = [
  {
    questionID: 0,
    questionType: 'ERROR_TYPE',
    questionText: 'Technical Error! Try again later',
    answer: true,
    answerList: ['YES', 'NO', 'STOP'],
    questionLevel: '1',
  },
];

const WebForm = (props) => {
  const messageList = useRef();

  // State
  const [currentQuestions, setCurrentQuestions] = useState(INITIAL_ERROR_QUESTION);
  const [loading, setLoading] = useState(true);
  const [surveyAnswer, setSurveyAnswer] = useState([]);
  const [isCaptchaValid, setIsCaptchaValid] = useState(true);
  const [captchaLoaded, setCaptchaLoaded] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const [accountId, setAccountId] = useState(null);
  const [accountLogo, setAccountLogo] = useState(null);
  const [surveyTitle, setSurveyTitle] = useState('');
  const [progress, setProgress] = useState(0);
  const [questionGroups, setQuestionGroups] = useState([]);
  const [currentQuestionGroup, setCurrentQuestionGroup] = useState(0);
  const [surveyCompleteMessage, setSurveyCompleteMessage] = useState(
    'Thank you for your Participation',
  );
  const [allQuestions, setAllQuestions] = useState({});
  const [latestNonEmptySurveyAnswer, setLatestNonEmptySurveyAnswer] = useState([]);

  // Update body tag based on join code
  useEffect(() => {
    //console.log('accountId', accountId);
    if (accountId == '73450') {
      document.body.classList.add('tappi-custom');
    } else {
      document.body.classList.remove('tappi-custom');
    }
  }, [accountId]);

  const initialLoadDone = useRef(false);

  // Socket and fetch logic
  useEffect(() => {
    if (!isInvalid && props.joinCode) {
      const socket = socketIOClient(process.env.REACT_APP_SOCKET_URL);

      socket.on('connect', () => {
        console.log('SOCKET CONNECTED');
        if (!initialLoadDone.current) {
          initialLoadDone.current = true;
          fetchFirstQuestion();
        }
      });
      socket.on('newQuestion', (data) => {
        triggerNextQuestion(data);
      });

      socket.on('disconnect', (reason) => {
        console.log('SOCKET DISCONNECTED:', reason);
      });

      socket.io.on('reconnect', () => {
        console.log('SOCKET RECONNECTED');
      });

      return () => {
        socket.disconnect();
      };
    }
    // eslint-disable-next-line
  }, [isInvalid, props.joinCode, props.commId]);

  // Remove the zendesk widget on webforms
  useEffect(() => {
    const elem = document.querySelector('#launcher');
    if (elem != null && elem.style.display !== 'none') elem.style.display = 'none';
  }, []);

  const onCaptchaLoaded = () => {
    setCaptchaLoaded(true);
  };

  const onCaptchaVerify = (response) => {
    if (response) {
      setIsCaptchaValid(true);
    }
  };

  // const reloadPageWithLimit = () => {
  //   const maxReloads = 3;
  //   let reloadCount = parseInt(localStorage.getItem('reloadCount') ?? '0') || 0;

  //   if (reloadCount < maxReloads) {
  //     localStorage.setItem('reloadCount', String(reloadCount + 1));
  //     const currentUrl = window.location.origin;
  //     window.location.href = `${currentUrl}/?j=${props.joinCode}&m=${props.commId}`;
  //   } else {
  //     console.warn("Reload limit reached. The page won't be reloaded again.");
  //     setErrorMessage('Something went wrong');
  //     setShowError(true);
  //     setTimeout(() => {
  //       setErrorMessage('');
  //       setShowError(false);
  //     }, 10000);
  //   }
  // };

  useEffect(() => {
    surveyAnswerRef.current = surveyAnswer;
    if (surveyAnswer.length > 0) {
      setLatestNonEmptySurveyAnswer(surveyAnswer);
    }
  }, [surveyAnswer]);

  const surveyAnswerRef = useRef(surveyAnswer);

  console.log('surveyAnswer after useRef', surveyAnswerRef.current);

  const onQuestionValueChanged = (responseText, questionID, questionLevel) => {
    const currentSurveyAnswer = surveyAnswerRef.current;
    //console.log('currentSurveyAnswer in onQuestionValueChanged', currentSurveyAnswer);
    //console.log('questionID in onQuestionValueChanged', questionID);
    const objectIndex = currentSurveyAnswer.findIndex(
      (question) => Number(question?.questionID) === Number(questionID),
    );
    const newSurveyAnswer = [...currentSurveyAnswer];

    if (objectIndex === -1) {
      // Attempt to recover by finding the question in allQuestions
      console.warn(
        `Question with ID ${questionID} not found in surveyAnswer. Attempting to recover from allQuestions.`,
      );
      const missingQuestion = allQuestions.webQuestionList?.find(
        (q) => Number(q.questionID) === Number(questionID),
      );
      if (missingQuestion) {
        const newEntry = {
          responseText,
          questionID,
          questionLevel,
          isFinal: missingQuestion.lastQuestion || false,
          required: missingQuestion.required || false,
          questionType: missingQuestion.questionType,
        };
        const newSurveyAnswer = [...currentSurveyAnswer, newEntry];
        setSurveyAnswer(newSurveyAnswer);
        calculateProgress(newSurveyAnswer, allQuestions);
        return;
      }
      console.error('Question not found in surveyAnswer or allQuestions');
      return;
    }
    newSurveyAnswer[objectIndex] = {
      responseText,
      questionID,
      questionLevel,
      isFinal: newSurveyAnswer[objectIndex].isFinal,
      required: newSurveyAnswer[objectIndex].required,
      questionType: newSurveyAnswer[objectIndex].questionType,
    };

    setSurveyAnswer(newSurveyAnswer);
    calculateProgress(newSurveyAnswer, allQuestions);
  };

  const validateEmail = (email) => {
    return email.match(
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
  };

  const validatePhone = (phone) => {
    const matchPhone = phone.match(/^[0-9-+\s]+$/);
    if (matchPhone) if (phone.length >= 9 && phone.length <= 14) return true;
    return false;
  };

  const validateQuestionAnswer = (answer) => {
    let answersReady = true;
    let answerValue = answer?.responseText?.value;

    // check integer choice answer
    if (answer?.questionType === 'OPEN_ENDED_INTEGER') {
      let currQuestion = null;
      questionGroups.forEach((questionGroup) => {
        if (currQuestion === null)
          currQuestion = questionGroup.find((question) => {
            return question.questionID === answer.questionID;
          });
      });
      let lowerLimit = 0;
      let upperLimit = 2047483647;
      if (currQuestion?.validationRule != null) {
        const validationValue = currQuestion.validationRule;
        const arr = validationValue.split('-');
        lowerLimit = parseInt(arr[0]);
        upperLimit = parseInt(arr[1]);
      }
      if (Number(answerValue) < lowerLimit || Number(answerValue > upperLimit)) {
        answersReady = false;
        setLoading(false);
        setErrorMessage('Please give answers within the specified ranges');
        setShowError(true);
        setTimeout(() => {
          setErrorMessage('');
          setShowError(false);
        }, 8000);
      }
    }

    // open ended validation
    if (answer?.questionType === 'OPEN_ENDED') {
      // get question
      let currQuestion = null;
      questionGroups.forEach((questionGroup) => {
        if (currQuestion === null)
          currQuestion = questionGroup.find((question) => {
            return question.questionID === answer.questionID;
          });
      });
      let passedValidation = true;
      if (currQuestion?.inputType === 'EMAIL') {
        if (!validateEmail(answerValue)) {
          passedValidation = false;
          currQuestion.error = true;
          currQuestion.errorMessage = 'Enter a valid email';
        } else {
          delete currQuestion.error;
          delete currQuestion.errorMessage;
        }
      } else if (currQuestion?.inputType === 'PHONE') {
        if (!validatePhone(answerValue)) {
          passedValidation = false;
          currQuestion.error = true;
          currQuestion.errorMessage = 'Enter a valid phone number (eg 0721...)';
        } else {
          delete currQuestion.error;
          delete currQuestion.errorMessage;
        }
      } else if (currQuestion?.inputType === 'EMAIL_PHONE') {
        if (validatePhone(answerValue) || validateEmail(answerValue)) {
          delete currQuestion.error;
          delete currQuestion.errorMessage;
        } else {
          passedValidation = false;
          currQuestion.error = true;
          currQuestion.errorMessage = 'Enter a valid phone number (eg 0721...) or Email';
        }
      }

      if (!passedValidation) {
        answersReady = false;
        setLoading(false);
        setErrorMessage('Some questions have not been properly answered');
        setShowError(true);
        setTimeout(() => {
          setErrorMessage('');
          setShowError(false);
        }, 8000);
      }
    }

    if (answer?.required === true && answer?.responseText?.text?.length < 1) {
      if (
        answer?.questionType !== 'MESSAGE_END' &&
        answer?.questionType !== 'ERROR_TYPE' &&
        answer?.questionType !== 'END_MESSAGE' &&
        answer?.questionType !== 'INTRO_MESSAGE_MESSAGE' &&
        answer?.questionType !== 'MESSAGE'
      ) {
        answersReady = false;
        setLoading(false);
        setErrorMessage('Please give an answer for all questions marked with *');
        setShowError(true);
        setTimeout(() => {
          setErrorMessage('');
          setShowError(false);
        }, 8000);
      }
    }

    return answersReady;
  };

  // const handleIncentives = async () => {
  //   const isAdaptIt = props.joinCode === 'PUH97T';
  //   if (isAdaptIt) {
  //     try {
  //       axios.post('http://localhost:4041/api/0.1.0/incentives', {
  //         name: props.commId,
  //         email: props.commId,
  //       });
  //     } catch (error) {
  //       console.log('Error handling incentives:', error);
  //     }
  //   }
  // };

  // Submits the current question's answer and saves progress
  const onSubmitFeedback = async () => {
    // grouped questions logic
    if (currentQuestionGroup < questionGroups.length - 1) {
      // check if all current questions are answered
      const newIndex = currentQuestionGroup + 1;
      let answersReady = true;
      const neededAnswerQuestions = questionGroups[currentQuestionGroup].map((q) => q.questionID);
      surveyAnswer.forEach((answer) => {
        if (neededAnswerQuestions.includes(answer.questionID)) {
          if (answersReady) answersReady = validateQuestionAnswer(answer);
        }
      });

      if (answersReady === false) return;
      setCurrentQuestionGroup(newIndex);
      setCurrentQuestions(questionGroups[newIndex]);
      calculateProgress(surveyAnswer, allQuestions);
      window.scrollTo(0, 0);
    } else if (surveyAnswer.length > 0) {
      // fully answered section logic
      let answersReady = true;
      surveyAnswer.forEach((answer) => {
        if (answersReady) answersReady = validateQuestionAnswer(answer);
      });

      if (answersReady === false) return;

      setLoading(true);

      try {
        const questionBody = {
          commId: props.commId,
          surveyJoinCode: props.joinCode,
          responseList: surveyAnswer.map((obj) => ({
            ...obj,
            responseText: {
              text: obj.responseText.text.toString(),
              value: obj.responseText.value.toString(),
            },
          })),
        };

        setCurrentQuestionGroup(0);
        setCurrentQuestions([]);
        setProgress(0);

        // submit the answer
        const questionResponse = await getQuestionData(questionBody);
        if (questionResponse?.data?.Metadata?.message === 'complete') {
          setCurrentQuestions([]);
          setLoading(false);
          setProgress(100);
          setSurveyCompleted(true);
          setSurveyCompleteMessage(questionResponse?.data?.Data?.text || '');
          setCurrentQuestionGroup(0);
          //handleIncentives();
        }
      } catch (error) {
        let currentQuestionsErr = '';
        if (error.response) {
          currentQuestionsErr = [
            {
              questionID: 0,
              questionType: 'ERROR_TYPE',
              questionText: error.response,
              answer: true,
              answerList: ['YES', 'NO', 'STOP'],
              questionLevel: '1',
            },
          ];
        } else {
          currentQuestionsErr = [
            {
              questionID: 0,
              questionType: 'ERROR_TYPE',
              questionText: 'Technical Error! Try again later',
              answer: true,
              answerList: ['YES', 'NO', 'STOP'],
              questionLevel: '1',
            },
          ];
        }
        setCurrentQuestions(currentQuestionsErr);
      } finally {
        setSurveyAnswer([]);
      }
    } else {
      setErrorMessage('Please give an answer');
      setShowError(true);
      setTimeout(() => {
        setErrorMessage('');
        setShowError(false);
      }, 5000);
    }
  };

  // Fetches the first question
  const fetchFirstQuestion = async () => {
    setLoading(true);
    if (!props.joinCode || !props.commId) {
      setIsInvalid(true);
      return;
    }
    setIsInvalid(false);
    try {
      const questionBody = {
        commId: props.commId,
        surveyJoinCode: props.joinCode,
        responseList: [
          {
            questionID: 0,
            responseText: {
              value: props.joinCode,
              text: props.joinCode,
            },
            questionLevel: '1',
          },
        ],
        surveyProgressMetadata: props.userMetadata,
      };
      setLoading(true);
      await getQuestionData(questionBody);
    } catch (error) {
      let currentQuestionsErr = '';
      if (error.response) {
        currentQuestionsErr = [
          {
            questionID: 0,
            questionType: 'ERROR_TYPE',
            questionText: error.response,
            answer: true,
            answerList: ['YES', 'NO', 'STOP'],
            questionLevel: '1',
          },
        ];
        setErrorMessage(
          getNestedObjectSafely(error, 'response', 'data', 'message') ||
            'Technical error! Try again later.',
        );
        setShowError(true);
        setTimeout(() => {
          setErrorMessage('');
          setShowError(false);
        }, 10000);
      } else {
        currentQuestionsErr = [
          {
            questionID: 0,
            questionType: 'ERROR_TYPE',
            questionText: 'Technical error! Try again later.',
            answer: true,
            answerList: ['YES', 'NO', 'STOP'],
            questionLevel: '1',
          },
        ];
      }
      setCurrentQuestions(currentQuestionsErr);
    }
  };

  const scrollToMessageBottom = () => {
    if (messageList && messageList.current) {
      messageList.current.scrollTop =
        messageList.current.scrollHeight - messageList.current.clientHeight;
    }
  };

  const onFetchLogo = async (accountId) => {
    try {
      const companyLogo = await fetchBusinessData(accountId);
      setAccountLogo(companyLogo.data.Data.logo);
    } catch (exception) {
      console.log('fetch company logo:', exception);
    }
  };

  const calculateProgress = (surveyAnswerArg = surveyAnswer, allQuestionsArg = allQuestions) => {
    const hasStoppedOrDeclined = surveyAnswerArg.some(
      (answer) =>
        answer?.responseText?.text?.toUpperCase() === 'STOP' ||
        answer?.responseText?.text?.toUpperCase() === 'NO',
    );

    if (hasStoppedOrDeclined) {
      setProgress(100);
      return;
    }

    let numOfQuestions = allQuestionsArg?.webQuestionList?.length;
    if (numOfQuestions > 0) {
      let hasLast = false;
      if (
        allQuestionsArg.webQuestionList.find((question) => question.lastQuestion === true) != null
      )
        hasLast = true;
      let totalNumber = allQuestionsArg.webQuestionList[0].totalQuestions;
      const firstNumber = allQuestionsArg.webQuestionList[0].questionNumber;
      const lastNumber = allQuestionsArg.webQuestionList[numOfQuestions - 1].questionNumber;
      if (hasLast) {
        totalNumber = lastNumber;
      }
      const percentageChunk = ((numOfQuestions / totalNumber) * 100) / numOfQuestions;
      const lastPercentage = ((firstNumber - 1) / totalNumber) * 100;
      let answeredQuestions = 0;
      surveyAnswerArg.forEach((answer) => {
        if (
          answer?.questionType !== 'MESSAGE_END' &&
          answer?.questionType !== 'ERROR_TYPE' &&
          answer?.questionType !== 'END_MESSAGE' &&
          answer?.questionType !== 'INTRO_MESSAGE_MESSAGE' &&
          answer?.questionType !== 'MESSAGE'
        ) {
          if (answer?.responseText?.text?.length > 0) {
            answeredQuestions += 1;
          }
        } else {
          answeredQuestions += 1;
        }
      });
      setProgress(Math.round(lastPercentage + answeredQuestions * percentageChunk));
    }
  };

  const triggerNextQuestion = async (data) => {
    const decodedData = decodeURI(data);
    const nextQuestions = JSON.parse(decodedData);

    if (nextQuestions.to === props.commId && loading === true) {
      if (
        getNestedObjectSafely(nextQuestions, 'webQuestionList') &&
        (nextQuestions.webQuestionList[0].questionType === 'MESSAGE_END' ||
          nextQuestions.webQuestionList[0].questionType === 'END_MESSAGE')
      ) {
        setCurrentQuestions([nextQuestions.webQuestionList[0]]);
        setLoading(false);
        setAccountId(nextQuestions.accountID);
        setProgress(nextQuestions.progress);
        setSurveyTitle(nextQuestions.surveyTitle);
        setSurveyCompleted(true);
        setAllQuestions(nextQuestions);
        return;
      }
      // attempt to create question groups
      const finalGroup = [];
      let currentGroup = [];

      const surveyAnswerArr = [];
      if (nextQuestions.webQuestionList && nextQuestions.webQuestionList.length > 0) {
        nextQuestions.webQuestionList.forEach((question) => {
          surveyAnswerArr.push({
            responseText: { value: '', text: '' },
            isFinal: question.lastQuestion,
            required: question.required,
            questionID: question.questionID,
            questionLevel: question.questionLevel,
            questionType: question.questionType,
          });
          // console.log('surveyAnswerArr after push', surveyAnswerArr);
          if (
            question.questionType === 'OPEN_ENDED_RANGE_0_10' ||
            question.questionType === 'MESSAGE'
          ) {
            currentGroup.push(question);
          } else if (question.questionType === 'OPEN_ENDED' && currentGroup.length > 1) {
            currentGroup.push(question);
            finalGroup.push(currentGroup);
            currentGroup = [];
          } else {
            currentGroup.push(question);
            finalGroup.push(currentGroup);
            currentGroup = [];
          }
        });
        if (currentGroup.length > 0) finalGroup.push(currentGroup);
        let ongoingGroups = [];
        const mergedGroups = [];
        finalGroup.forEach((group) => {
          const rangeNumber =
            group.filter((question) => question.questionType === 'OPEN_ENDED_RANGE_0_10').length ||
            0;
          const openNumber =
            group.filter((question) => question.questionType === 'OPEN_ENDED').length || 0;
          if (rangeNumber > 1 && openNumber === 1) {
            if (ongoingGroups.length > 0) {
              mergedGroups.push(ongoingGroups);
            }
            ongoingGroups = [];
            mergedGroups.push(group);
          } else {
            ongoingGroups = [...ongoingGroups, ...group];
          }
        });
        if (ongoingGroups.length > 0) {
          mergedGroups.push(ongoingGroups);
        }

        setCurrentQuestions(mergedGroups[0]);
        setLoading(false);
        setAccountId(nextQuestions.accountID);
        setProgress(nextQuestions.webQuestionList[0].progress || 0);
        setSurveyTitle(nextQuestions.surveyTitle);
        setSurveyAnswer(surveyAnswerArr);
        setQuestionGroups(mergedGroups);
        setAllQuestions(nextQuestions);
        calculateProgress(surveyAnswerArr, nextQuestions);
      }
      if (accountLogo === null) {
        await onFetchLogo(nextQuestions.accountID);
      }
    }
  };

  const user = props.userMetadata?.user;
  const isTappi = accountId == '73450';
  const isNutriGo = accountId == '132990';
  // Render
  return (
    <section
      className={`web-form-container ${
        props.joinCode === ('ZNTR6S8Y' || '7HBR1G') ? 'kq-font' : 'web-form-container-font'
      }`}
    >
      <div>
        <div className="logo-section">
          {accountLogo ? (
            <img
              onError={() => setAccountLogo(null)}
              className="company-logo"
              alt="company logo"
              src={accountLogo}
            />
          ) : (
            <div className="company-logo">&nbsp;</div>
          )}
        </div>
        {isInvalid && <Navigate to="/404" />}
        {surveyCompleted && <SurveySubmitted message={surveyCompleteMessage} />}
        {isCaptchaValid && !isInvalid && !surveyCompleted && (
          <>
            <section className="web-view">
              {loading && (
                <div
                  style={{
                    zIndex: '999999',
                    background: 'white',
                  }}
                >
                  <div className="loader-container">
                    <Loader size={50} color="#2196f3" loading={loading && captchaLoaded} />
                  </div>
                </div>
              )}

              {!loading && (
                <div className="web-container content-container">
                  {!props.isAnonymousUser && (
                    <span className="x-small align-self-start" style={{ marginRight: '0.5rem' }}>
                      You are completing this survey as {props.commId}
                    </span>
                  )}
                  {document.referrer !== '' ? null : (
                    <h2 className="survey-title">
                      <strong>
                        {surveyTitle.toLowerCase() === 'survey error'
                          ? ''
                          : surveyTitle.replace(/\{user\}/gi, user)}
                      </strong>
                    </h2>
                  )}
                  <div id="webFormProgress">
                    <div
                      id={isTappi ? 'tappi-webform-bar' : 'webFormBar'}
                      style={{ width: `${progress || 0}%` }}
                    >
                      {progress || 0}%
                    </div>
                  </div>
                  {currentQuestions.map((question) => {
                    let questionValue = '';
                    let questionResponseText = {
                      value: '',
                      text: '',
                    };
                    const questionFound = surveyAnswer.find(
                      (answer) => String(answer.questionID) === String(question.questionID),
                    );
                    if (questionFound != null) {
                      questionValue = questionFound.responseText.value;
                      questionResponseText = questionFound.responseText;
                    }

                    let questionText = question.questionText;
                    console.log('Original question text:', questionText);
                    if (!questionText.includes('<strong>')) {
                      if (question.required === true) {
                        if (questionText.includes('*')) {
                          questionText = questionText.replace(
                            '*',
                            '<span style="color: red; font-size: 1.5em;margin-right: 0.2rem">*</span>',
                          );
                        } else {
                          questionText = `<span style="color: red;font-size: 1.5em;margin-right: 0.2rem">*</span>${questionText}`;
                        }
                      }
                      questionText = `<strong>${questionText}</strong>`;
                    }

                    return (
                      <React.Fragment key={question.questionID}>
                        {question.questionType !== 'QuestionError' && document.referrer
                          ? window.parent.postMessage(
                              {
                                ok: true,
                                message: 'Webform is valid',
                                code: 'WEBFORM_VALID',
                                type: 'webform:onvalid',
                              },
                              document.referrer,
                            )
                          : null}
                        {question.questionType === 'OPEN_ENDED_NPS_0_10' ||
                        question.questionType === 'NPS_CHOICE' ? (
                          <NpsQuestion
                            question={{ ...question, questionText }}
                            value={questionValue}
                            onChange={onQuestionValueChanged}
                            user={user}
                          />
                        ) : question.questionType === 'CUSTOMER_EFFORT_SCORE' ||
                          question.questionType === 'CUSTOMER_EFFORT_SCORE_0_7' ? (
                          <CESQuestion
                            question={{ ...question, questionText }}
                            value={questionValue}
                            onChange={onQuestionValueChanged}
                            user={user}
                          />
                        ) : question.questionType === 'OPEN_ENDED_NPS' ||
                          question.questionType === 'OPEN_ENDED' ? (
                          <OpenEnded
                            question={{ ...question, questionText }}
                            value={questionValue}
                            onChange={onQuestionValueChanged}
                            user={user}
                          />
                        ) : question.questionType === 'OPEN_ENDED_RANGE_0_10' ||
                          question.questionType === 'RANGE_0_10' ? (
                          <RangeQuestion
                            question={{ ...question, questionText }}
                            value={questionValue}
                            onChange={onQuestionValueChanged}
                          />
                        ) : question.questionType === 'MULTIPLE_CHOICE_SINGLE_SELECT' ||
                          question.questionType === 'INTRO_MESSAGE_MULTIPLE_CHOICE' ? (
                          <SingleSelect
                            question={{ ...question, questionText }}
                            value={questionValue}
                            onChange={onQuestionValueChanged}
                            user={user}
                            joinCode={props.joinCode}
                          />
                        ) : question.questionType === 'CUSTOMER_SATISFACTION_SCORE' ? (
                          <CSAT
                            question={{ ...question, questionText }}
                            value={questionValue}
                            onChange={onQuestionValueChanged}
                          />
                        ) : question.questionType === 'OPEN_ENDED_INTEGER' ? (
                          <IntegerChoice
                            question={{ ...question, questionText }}
                            value={questionValue}
                            onChange={onQuestionValueChanged}
                          />
                        ) : question.questionType === 'RESOLUTION_SCORE' ? (
                          <ResolutionScore
                            question={{ ...question, questionText }}
                            value={questionValue}
                            onChange={onQuestionValueChanged}
                          />
                        ) : question.questionType === 'MULTIPLE_CHOICE_MULTIPLE_SELECT' ? (
                          <MultipleSelect
                            question={{ ...question, questionText }}
                            questionValue={questionResponseText}
                            onChange={onQuestionValueChanged}
                          />
                        ) : question.questionType === 'OPEN_ENDED_DATE' ? (
                          <OpenEndedDate
                            question={{ ...question, questionText }}
                            value={questionValue}
                            onChange={onQuestionValueChanged}
                          />
                        ) : question.questionType == 'OPEN_ENDED_YEAR_YYYY' ? (
                          <YearQuestion
                            question={{ ...question, questionText }}
                            value={questionValue}
                            onChange={onQuestionValueChanged}
                          />
                        ) : question.questionType === 'MESSAGE_END' ||
                          question.questionType === 'ERROR_TYPE' ||
                          question.questionType === 'END_MESSAGE' ||
                          question.questionType === 'INTRO_MESSAGE_MESSAGE' ||
                          question.questionType === 'MESSAGE' ||
                          question.questionType === 'QuestionError' ? (
                          <FallBack question={{ ...question, questionText }} />
                        ) : (
                          <OpenEnded
                            question={{ ...question, questionText }}
                            value={questionValue}
                            onChange={onQuestionValueChanged}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                  {currentQuestions[0]?.questionType !== 'QuestionError' && (
                    <button
                      className={
                        isNutriGo || isTappi ? 'btn btn-primary tappi-btn' : 'submit-button'
                      }
                      onClick={onSubmitFeedback}
                      type="button"
                    >
                      {currentQuestions.find((question) => question?.lastQuestion === true)
                        ? 'Submit'
                        : 'Next'}
                    </button>
                  )}
                </div>
              )}
            </section>
          </>
        )}
        {showError && <div className="footer">{errorMessage}</div>}
        {!isCaptchaValid && !isInvalid && !surveyCompleted && (
          <div className="content-container captcha">
            <div className="loader-container">
              <Loader size={50} color="#2196f3" loading={!captchaLoaded} />
            </div>
            {captchaLoaded && <h2> Complete the captcha below to access this survey</h2>}
            <Recaptcha
              onloadCallback={onCaptchaLoaded}
              sitekey="6LdQR9YUAAAAAM9u_d34yE40lG0e8WqYrY8M4xhw"
              render="explicit"
              verifyCallback={onCaptchaVerify}
            />
            <div className="header">
              <a href="https://www.ajua.com">
                <img src={logo} alt="Ajua Logo" className="logo-header" />
                <span>Powered by Ajua</span>
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default WebForm;
